import React, { useState, useRef, useEffect } from 'react';
import { 
  X, Upload, Camera, Lock, Loader2, FileText, 
  ScanLine, ArrowUp, Sparkles, ShieldCheck, Zap,
  CheckCircle2, AlertTriangle
} from 'lucide-react'; 
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// UI Components
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge'; 

// Context & Services
// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useFox } from '../context/FoxContext';

const ScanModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { language, t } = useLanguage(); // Dil fonksiyonunu al
  const { toast } = useToast();
  const { triggerFox } = useFox();
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanMessageIndex, setScanMessageIndex] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);

  // 🌍 Dinamik Tarama Adımları (Çeviri sistemine bağlı)
  // useLanguage hook'u sayesinde dil değişince burası da güncellenir
  const SCAN_STEPS = [
    t('scan_step_1') || "Initializing Neural Modules...",
    t('scan_step_2') || "OCR Layer Extraction...",
    t('scan_step_3') || "Parsing Dates & Currency...",
    t('scan_step_4') || "Categorizing Document...",
    t('scan_step_5') || "Encrypting for Vault...",
    t('scan_step_6') || "Finalizing Results..."
  ];

  useEffect(() => {
    let interval;
    if (isUploading) {
        interval = setInterval(() => {
            setScanMessageIndex(prev => (prev + 1) % SCAN_STEPS.length);
        }, 1200); 
    }
    return () => clearInterval(interval);
  }, [isUploading, SCAN_STEPS.length]);

  const processFile = async (file) => {
    if (!file) return;
    
    // 🛡️ Alman Standartlarında Dosya Kontrolü (15MB Limit)
    if (file.size > 15 * 1024 * 1024) {
        toast({ 
          title: t('error_file_large') || "File Too Large", 
          description: t('error_file_large_desc') || "Max 15MB limit.", 
          variant: "destructive" 
        });
        return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setIsUploading(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language); // AI'nın hangi dilde analiz yapacağını söyler

    try {
      // API İsteği (Gerçek Upload)
      const response = await apiService.documents.upload(formData);
      const newDoc = response.data;

      toast({ 
        title: t('scan_success') || "Analysis Complete", 
        description: t('scan_success_desc') || "Document processed successfully.", 
        className: "bg-emerald-500 border-emerald-600 text-white" 
      });

      // Başarılı analiz sonrası sonuç sayfasına yönlendir
      setTimeout(() => {
        onClose();
        navigate('/analysis-result', { state: { document: newDoc } });
        URL.revokeObjectURL(objectUrl);
        setPreviewUrl(null);
        setIsUploading(false);
      }, 1000);

    } catch (error) {
      console.error("Upload Error:", error);
      setIsUploading(false);
      setPreviewUrl(null);
      
      if (error.response?.status === 403) {
        onClose();
        triggerFox("limit_reached_msg", { position: "top-center", mood: "shocked" });
      } else {
        toast({ 
          title: t('scan_error') || "Connection Failed", 
          description: t('scan_error_desc') || "Please check your internet and try again.", 
          variant: "destructive" 
        });
      }
    }
  };

  const handleFileSelect = (e) => processFile(e.target.files[0]);
  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files?.[0]) processFile(e.dataTransfer.files[0]);
  };

  const handleClose = () => { if (!isUploading) onClose(); };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-md p-0 gap-0 border-none rounded-[3rem] overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.8)] bg-[#020617] outline-none text-white ring-1 ring-white/10"
        style={{ width: '95vw', minHeight: isUploading ? '600px' : 'auto' }}
        onInteractOutside={(e) => isUploading && e.preventDefault()}
      >
        <DialogTitle className="sr-only">Fox Neural Scanner</DialogTitle>

        <div className="w-full h-full flex flex-col relative font-sans">
          
          {/* Status Bar */}
          <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-start z-50 pointer-events-none">
             <div className="bg-[#0f172a]/80 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10 flex items-center gap-2 shadow-lg">
                <div className={`w-2 h-2 rounded-full ${isUploading ? 'bg-orange-500 animate-pulse' : 'bg-emerald-500'}`} />
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-300">
                    {isUploading ? (t('ai_active') || 'Fox AI: Active') : (t('ready_to_scan') || 'Ready to Scan')}
                </span>
             </div>
             {!isUploading && (
                 <button onClick={handleClose} className="pointer-events-auto p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors backdrop-blur-md text-slate-400 hover:text-white">
                    <X size={16}/>
                 </button>
             )}
          </div>

          <AnimatePresence mode="wait">
            {!isUploading ? (
              /* --- SEÇİM EKRANI --- */
              <motion.div 
                key="idle"
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="p-10 flex flex-col items-center justify-center min-h-[500px]"
                onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
              >
                <div className={`w-32 h-32 rounded-[2.5rem] mb-8 flex items-center justify-center relative transition-all duration-500 group ${isDragOver ? 'bg-orange-600 shadow-[0_0_60px_rgba(234,88,12,0.6)] scale-110' : 'bg-[#0f172a] border border-white/10 shadow-2xl'}`}>
                   {isDragOver ? (
                       <ArrowUp size={48} className="text-white animate-bounce" />
                   ) : (
                       <>
                         <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/20 to-transparent rounded-[2.5rem] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                         <ScanLine size={48} className="text-orange-500 relative z-10" />
                       </>
                   )}
                </div>

                <div className="text-center space-y-3 mb-10 relative z-10">
                    <h3 className="text-3xl font-black text-white tracking-tighter uppercase italic">
                        {isDragOver ? (t('release_to_scan') || "Release") : (t('initiate_scan') || "Initiate Scan")}
                    </h3>
                    <p className="text-slate-400 text-sm font-medium px-4 leading-relaxed">
                        {t('scan_modal_desc') || "Upload documents for automated AI analysis."}<br/>
                        <span className="text-[10px] uppercase tracking-widest opacity-60">JPG, PNG, PDF (Max 15MB)</span>
                    </p>
                </div>

                <div className="w-full space-y-4 text-center relative z-10">
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*,.pdf" onChange={handleFileSelect} />
                  <input type="file" ref={cameraInputRef} className="hidden" accept="image/*" capture="environment" onChange={handleFileSelect} />
                  
                  <Button 
                    onClick={() => cameraInputRef.current.click()}
                    className="w-full h-16 text-xs font-black uppercase tracking-[0.2em] bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-2xl shadow-xl active:scale-[0.98] transition-all group"
                  >
                    <Camera className="mr-3 h-5 w-5 group-hover:rotate-12 transition-transform" /> {t('camera_capture') || 'Camera'}
                  </Button>

                  <Button 
                    variant="ghost"
                    onClick={() => fileInputRef.current.click()}
                    className="w-full h-14 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-white hover:bg-white/5 rounded-2xl border border-white/5 transition-all"
                  >
                    <Upload className="mr-3 h-4 w-4" /> {t('upload_from_device') || 'Select File'}
                  </Button>
                </div>
              </motion.div>
            ) : (
              /* --- TARAMA EKRANI --- */
              <motion.div 
                key="scanning"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden bg-[#020617]"
              >
                {previewUrl && (
                    <motion.div 
                      initial={{ scale: 1.2, opacity: 0 }} animate={{ scale: 1.05, opacity: 0.2 }}
                      className="absolute inset-0 bg-cover bg-center blur-3xl saturate-200"
                      style={{ backgroundImage: `url(${previewUrl})` }}
                    />
                )}

                {/* Doc Preview with Laser */}
                <div className="relative w-[70%] aspect-[3/4.2] rounded-[2rem] overflow-hidden shadow-[0_0_80px_rgba(0,0,0,0.8)] border border-white/10 bg-[#0f172a] z-10 group">
                    {previewUrl ? (
                        <img src={previewUrl} alt="Scan" className="w-full h-full object-cover opacity-60 grayscale group-hover:grayscale-0 transition-all duration-1000" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-700"><FileText size={60} /></div>
                    )}

                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                        <motion.div 
                            animate={{ top: ["-10%", "110%", "-10%"] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            className="w-full h-1.5 bg-orange-500 shadow-[0_0_30px_#f97316] absolute z-20"
                        />
                    </div>
                </div>

                {/* Progress Terminal */}
                <div className="mt-12 z-20 w-full px-8">
                    <div className="bg-[#0f172a]/80 backdrop-blur-xl p-6 rounded-[2rem] border border-white/10 shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 left-0 h-1 bg-gradient-to-r from-blue-600 to-orange-400 w-full animate-progress-indeterminate origin-left"></div>

                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <Loader2 className="text-orange-500 animate-spin" size={18} />
                                <span className="text-[10px] font-black uppercase tracking-[0.25em] text-white">BüroFuchs Engine</span>
                            </div>
                            <Badge className="bg-emerald-500/10 text-emerald-500 border-none text-[8px] animate-pulse uppercase">{t('ai_analyzing') || 'AI Analyzing'}</Badge>
                        </div>
                        
                        <div className="h-6 overflow-hidden relative">
                            <AnimatePresence mode="wait">
                              <motion.p 
                                key={scanMessageIndex}
                                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                                className="text-slate-300 text-[11px] font-bold tracking-wide font-mono absolute w-full"
                              >
                                 {`> ${SCAN_STEPS[scanMessageIndex]}`}
                              </motion.p>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScanModal;